"use client"

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getTransactionDetails } from '../../services/paymentService';
import { 
  FiArrowLeft, FiDollarSign, FiCalendar, FiCheckCircle, FiAlertCircle,
  FiCreditCard, FiRefreshCw, FiUser, FiFileText, FiChevronRight, FiDownload, FiClock, FiInfo, FiHash
} from 'react-icons/fi';
import { motion } from 'framer-motion';
// ✅ NOUVEAUX IMPORTS POUR L'EXPORT PDF
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// --- Fonctions Utilitaires ---
const formatCurrency = (value, currency = 'TND') => {
  if (value === undefined || value === null) return '--';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency.toUpperCase() }).format(value || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '--';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (error) { return '--'; }
};


// --- Sous-composants UI ---
const DetailRow = ({ icon, label, value, valueClassName = "text-white" }) => (
    <div className="flex justify-between items-center py-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3 text-slate-400">
            {icon}
            <span className="text-sm">{label}</span>
        </div>
        <span className={`font-semibold text-sm text-right ${valueClassName}`}>{value}</span>
    </div>
);

const LoadingSkeleton = () => (
    <div className="max-w-4xl mx-auto animate-pulse">
        <div className="h-6 bg-slate-700 rounded w-1/4 mb-12"></div>
        <div className="bg-slate-800/50 p-8 rounded-2xl space-y-6">
            <div className="h-16 bg-slate-700 rounded w-1/3 mx-auto"></div>
            <div className="h-4 bg-slate-700 rounded w-full"></div>
            <div className="h-4 bg-slate-700 rounded w-full"></div>
            <div className="h-4 bg-slate-700 rounded w-2/3"></div>
        </div>
    </div>
);

const ErrorState = ({ error, onRetry }) => (
    <div className="max-w-4xl mx-auto">
      <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiAlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Erreur de chargement</h2>
        <p className="text-slate-400 mb-6">{error}</p>
        <button
          onClick={onRetry}
          className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors mx-auto"
        >
          <FiRefreshCw className="w-5 h-5" />
          Réessayer
        </button>
      </div>
    </div>
);


// --- COMPOSANT PRINCIPAL ---
const TransactionDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const receiptRef = useRef(null); // ✅ Référence pour le modèle PDF

  useEffect(() => {
    const fetchTransaction = async (transactionId) => {
      if (!transactionId) {
        setError('ID de transaction manquant.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await getTransactionDetails(transactionId);
        const txData = response.data || response;
        if (!txData) throw new Error('Aucune donnée de transaction trouvée.');
        setTransaction({
          ...txData,
          amount: parseFloat(txData.amount) || 0,
          status: txData.status || 'inconnu',
          createdAt: txData.created_at || txData.createdAt,
          reference: txData.reference || `TRX-${txData.id?.substring(0, 8) || 'N/A'}`
        });
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du chargement des détails.';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchTransaction(id);
    }
  }, [id, retryCount]);
  
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };
  
  // ✅ NOUVELLE FONCTION POUR L'EXPORT PDF
  const handleDownloadPDF = async () => {
    const receiptElement = receiptRef.current;
    if (!receiptElement) return;

    toast.info("Génération de votre reçu PDF...");

    try {
        const canvas = await html2canvas(receiptElement, { scale: 2, backgroundColor: '#1e293b', useCORS: true });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`recu-${transaction.reference || transaction.id}.pdf`);
        
        toast.success("Votre reçu a été téléchargé !");
    } catch (error) {
        toast.error("Impossible de générer le reçu PDF.");
    }
  };

  const StatusInfo = ({ status }) => {
    const statusMap = {
      succeeded: { text: 'Terminé', icon: <FiCheckCircle />, color: 'text-green-400' },
      completed: { text: 'Terminé', icon: <FiCheckCircle />, color: 'text-green-400' },
      pending: { text: 'En attente', icon: <FiClock />, color: 'text-amber-400' },
      failed: { text: 'Échoué', icon: <FiAlertCircle />, color: 'text-red-400' },
    };
    const currentStatus = statusMap[status] || { text: status, icon: <FiInfo />, color: 'text-slate-400' };
    return <div className={`flex items-center gap-2 text-lg font-semibold ${currentStatus.color}`}>{currentStatus.icon}{currentStatus.text}</div>;
  };

  if (loading) return <div className="p-6"><LoadingSkeleton /></div>;
  if (error) return <div className="p-6"><ErrorState error={error} onRetry={handleRetry} /></div>;
  if (!transaction) return <div className="p-6 text-center text-slate-400">Transaction introuvable.</div>;

  return (
    <>
      {/* ✅ MODÈLE INVISIBLE POUR LE REÇU PDF */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, fontFamily: 'sans-serif' }}>
          <div ref={receiptRef} style={{ width: '800px', padding: '50px', backgroundColor: '#1e293b', color: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155', paddingBottom: '20px' }}>
                  <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#f59e0b' }}>Liberty</h1>
                  <p style={{ fontSize: '16px', color: '#94a3b8' }}>REÇU DE TRANSACTION</p>
              </div>
              <div style={{ marginTop: '40px', textAlign: 'center' }}>
                  <p style={{ fontSize: '16px', color: '#94a3b8' }}>{transaction.description}</p>
                  <p style={{ fontSize: '52px', fontWeight: 'bold', margin: '10px 0', color: transaction.amount >= 0 ? '#4ade80' : '#f87171' }}>
                      {transaction.amount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount), transaction.currency)}
                  </p>
                  <p style={{ display: 'inline-block', padding: '6px 14px', borderRadius: '9999px', backgroundColor: '#2dd4bf20', color: '#2dd4bf', fontWeight: 'bold', fontSize: '14px' }}>
                      Paiement {transaction.status === 'succeeded' ? 'Terminé' : 'En attente'}
                  </p>
              </div>
              <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '1px solid #334155' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                          <tr style={{borderBottom: '1px solid #334155'}}><td style={{padding: '15px 0', color: '#94a3b8'}}>CLIENT</td><td style={{fontWeight: 'bold', textAlign: 'right'}}>{`${transaction.sender?.first_name || ''} ${transaction.sender?.last_name || ''}`}</td></tr>
                          <tr style={{borderBottom: '1px solid #334155'}}><td style={{padding: '15px 0', color: '#94a3b8'}}>DATE</td><td style={{fontWeight: 'bold', textAlign: 'right'}}>{formatDate(transaction.createdAt)}</td></tr>
                          <tr style={{borderBottom: '1px solid #334155'}}><td style={{padding: '15px 0', color: '#94a3b8'}}>RÉFÉRENCE</td><td style={{fontWeight: 'bold', textAlign: 'right', fontFamily: 'monospace'}}>{transaction.reference}</td></tr>
                          <tr style={{borderBottom: '1px solid #334155'}}><td style={{padding: '15px 0', color: '#94a3b8'}}>MÉTHODE</td><td style={{fontWeight: 'bold', textAlign: 'right', textTransform: 'capitalize'}}>{transaction.payment_method}</td></tr>
                      </tbody>
                  </table>
              </div>
              <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #334155', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
                  <p>Merci de faire confiance à Liberty. Pour toute question, contactez notre support.</p>
                  <p>Liberty &copy; {new Date().getFullYear()}</p>
              </div>
          </div>
      </div>

      {/* --- PAGE VISIBLE PAR L'UTILISATEUR --- */}
      <div className="min-h-screen bg-slate-900 text-white p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
    

          <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800/50 border border-slate-700 rounded-2xl shadow-2xl"
          >
              <header className="p-8 text-center border-b border-slate-700">
                  <p className="text-sm text-slate-400">{transaction.description || 'Transaction'}</p>
                  <p className={`text-5xl font-bold my-2 ${transaction.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {transaction.amount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount), transaction.currency)}
                  </p>
                  <StatusInfo status={transaction.status} />
              </header>

              <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                      <div className="space-y-1">
                          <h3 className="font-semibold text-amber-400 pb-2">Récapitulatif</h3>
                          <DetailRow icon={<FiFileText className="text-slate-500"/>} label="Référence" value={<span className="font-mono">{transaction.reference}</span>} />
                          <DetailRow icon={<FiCalendar className="text-slate-500"/>} label="Date et heure" value={formatDate(transaction.createdAt)} />
                          <DetailRow icon={<FiHash className="text-slate-500"/>} label="ID Paiement Stripe" value={<span className="font-mono text-xs">{transaction.payment_intent_id || 'N/A'}</span>} />
                      </div>
                      <div className="space-y-1">
                           <h3 className="font-semibold text-amber-400 pb-2">Détails du paiement</h3>
                          <DetailRow icon={<FiUser className="text-slate-500"/>} label="Client" value={`${transaction.sender?.first_name || ''} ${transaction.sender?.last_name || ''}`} />
                          <DetailRow icon={<FiCreditCard className="text-slate-500"/>} label="Méthode" value={<span className="capitalize">{transaction.payment_method || 'Inconnue'}</span>} />
                      </div>
                  </div>
              </div>
              
              <footer className="p-6 bg-slate-900/50 border-t border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-b-2xl">
                  <p className="text-xs text-slate-500 font-mono">ID Transaction: {transaction.id}</p>
                  <div className="flex items-center gap-3">
                      <button onClick={() => navigate(-1)} className="px-4 py-2 bg-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-600 flex items-center gap-2">
                          <FiArrowLeft className="w-4 h-4"/> Retour
                      </button>
                      <button 
                        onClick={handleDownloadPDF}
                        className="px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-500 flex items-center gap-2"
                      >
                        <FiDownload className="w-4 h-4"/> Télécharger en PDF
                      </button>
                  </div>
              </footer>
          </motion.div>
        </div>
        <ToastContainer theme="dark" position="bottom-right" />
      </div>
    </>
  );
};

export default TransactionDetails;